import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileAPI } from '../../services/api'
import { PASSWORD_MAX, PASSWORD_MIN, passwordFits } from '../../utils/passwordRule'
import { SESSIONS_QUERY_KEY } from './sessionsQueries'
import { wasCancelled, withConfirmation } from '../security/confirmCode'
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'

export default function SecuritySettingsCard() {
  const { t } = useTranslation()
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  const [successMessage, setSuccessMessage] = useState('')
  const queryClient = useQueryClient()

  const changePasswordMutation = useMutation({
    // VH-285: a current code first, when the last one is over five minutes old.
    mutationFn: (data) => withConfirmation(() => profileAPI.changePassword(data)),
    onSuccess: () => {
      setSuccessMessage(t('profile.passwordChanged', 'Password changed successfully'))
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
      setShowPasswordForm(false)
      setTimeout(() => setSuccessMessage(''), 5000)
      // VH-284: the other sessions ended with the password change.
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validate passwords match
    if (formData.new_password !== formData.confirm_password) {
      return
    }

    // VH-283: 12 to 200 characters, as the server checks
    if (!passwordFits(formData.new_password)) {
      return
    }

    changePasswordMutation.mutate(formData)
  }

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' }

    let score = 0
    if (password.length >= 8) score++
    if (password.length >= 12) score++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^a-zA-Z0-9]/.test(password)) score++

    const levels = [
      { min: 0, label: 'Very Weak', color: 'bg-red-500' },
      { min: 1, label: 'Weak', color: 'bg-orange-500' },
      { min: 2, label: 'Fair', color: 'bg-yellow-500' },
      { min: 3, label: 'Good', color: 'bg-blue-500' },
      { min: 4, label: 'Strong', color: 'bg-green-500' }
    ]

    for (let i = levels.length - 1; i >= 0; i--) {
      if (score >= levels[i].min) {
        return { score, label: levels[i].label, color: levels[i].color }
      }
    }
    return levels[0]
  }

  const passwordStrength = getPasswordStrength(formData.new_password)
  const passwordsMatch = formData.new_password === formData.confirm_password && formData.new_password.length > 0

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center">
          <LockClosedIcon className="h-6 w-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.security', 'Security')}
          </h3>
        </div>
      </div>
      <div className="card-body">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-green-700">{successMessage}</span>
          </div>
        )}

        {/* Password Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">{t('profile.password', 'Password')}</p>
              <p className="text-sm text-gray-500">
                {t('profile.passwordDescription', 'Secure your account with a strong password')}
              </p>
            </div>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="btn-secondary"
              >
                {t('profile.changePassword', 'Change Password')}
              </button>
            )}
          </div>

          {/* Password Change Form */}
          {showPasswordForm && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4 mt-4">
              {/* Current Password */}
              <div>
                <label className="form-label">{t('profile.currentPassword', 'Current Password')}</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={formData.current_password}
                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    className="form-input pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="form-label">{t('profile.newPassword', 'New Password')}</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    className="form-input pr-10"
                    minLength={PASSWORD_MIN}
                    maxLength={PASSWORD_MAX}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {/* Password Strength Indicator */}
                {formData.new_password && (
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('profile.passwordStrength', 'Strength')}: {passwordStrength.label}
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.passwordRequirements', '12 to 200 characters')}
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="form-label">{t('profile.confirmPassword', 'Confirm New Password')}</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    className={`form-input pr-10 ${
                      formData.confirm_password && !passwordsMatch ? 'border-red-500' : ''
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {formData.confirm_password && !passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {t('profile.passwordsDoNotMatch', 'Passwords do not match')}
                  </p>
                )}
                {passwordsMatch && (
                  <p className="text-xs text-green-500 mt-1 flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    {t('profile.passwordsMatch', 'Passwords match')}
                  </p>
                )}
              </div>

              {/* Error */}
              {changePasswordMutation.isError && !wasCancelled(changePasswordMutation.error) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center">
                  <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                  {(typeof changePasswordMutation.error?.response?.data?.detail === 'string' &&
                    changePasswordMutation.error.response.data.detail) ||
                    t('common.error', 'An error occurred')}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false)
                    setFormData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    })
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={
                    changePasswordMutation.isPending ||
                    !formData.current_password ||
                    !formData.new_password ||
                    !passwordsMatch ||
                    formData.new_password.length < 8
                  }
                  className="btn-primary"
                >
                  {changePasswordMutation.isPending
                    ? t('common.saving', 'Saving...')
                    : t('profile.updatePassword', 'Update Password')}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  )
}
